import React, { useState, useEffect } from 'react';
import { Box, Tab, Tabs, Typography, Alert } from '@mui/material';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import NotificationsIcon from '@mui/icons-material/Notifications';
import UpdateIcon from '@mui/icons-material/Update';
import LanguageIcon from '@mui/icons-material/Language';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../utils/textUtils';

const Settings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unauthorized, setUnauthorized] = useState(false);
  
  // Check if user has admin access
  const isAdmin = user && (user.role === 'Director' || user.role === 'admin');
  
  // Determine active tab based on current path
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/settings/profile')) return 0;
    if (path.includes('/settings/notifications') && !path.includes('notification-polling')) return 1;
    if (path.includes('/settings/notification-polling')) return 2;
    if (path.includes('/settings/language')) return 3;
    if (path.includes('/settings/admin') && isAdmin) return 4;
    return 0; // Default to profile
  };

  const [value, setValue] = useState(getActiveTab());
  
  // Redirect if trying to access admin panel without permission
  useEffect(() => {
    if (location.pathname.includes('/settings/admin') && !isAdmin) {
      setUnauthorized(true);
      navigate('/settings/profile');
    }
  }, [location.pathname, isAdmin, navigate]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    switch (newValue) {
      case 0:
        navigate('/settings/profile');
        break;
      case 1:
        navigate('/settings/notifications');
        break;
      case 2:
        navigate('/settings/notification-polling');
        break;
      case 3:
        navigate('/settings/language');
        break;
      case 4:
        // Only navigate to admin if user has permission
        if (isAdmin) {
          navigate('/settings/admin');
        } else {
          setUnauthorized(true);
          navigate('/settings/profile');
        }
        break;
      default:
        navigate('/settings/profile');
    }
  };

  return (
    <Box sx={{ width: '100%', px: 0 }}>
      {unauthorized && (
        <Alert severity="error" onClose={() => setUnauthorized(false)} sx={{ mb: 2 }}>
          {t('ANDA TIDAK MEMILIKI AKSES KE ADMIN PANEL. HANYA PENGGUNA DENGAN ROLE DIRECTOR YANG DAPAT MENGAKSES HALAMAN INI.')}
        </Alert>
      )}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ mb: 1.5 }}>
          {t('PENGATURAN')}
        </Typography>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', borderRadius: '4px 4px 0 0' }}>
          <Tabs 
            value={value} 
            onChange={handleChange} 
            aria-label={t('TAB PENGATURAN')}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: '48px' }}
          >
            <Tab 
              icon={<PersonIcon />} 
              label={t('PROFIL SAYA')}
              iconPosition="start" 
              sx={{ minHeight: '48px' }}
            />
            <Tab 
              icon={<NotificationsIcon />} 
              label={t('NOTIFIKASI')}
              iconPosition="start" 
              sx={{ minHeight: '48px' }}
            />
            <Tab 
              icon={<UpdateIcon />} 
              label={t('NOTIFIKASI OTOMATIS')}
              iconPosition="start" 
              sx={{ minHeight: '48px' }}
            />
            <Tab 
              icon={<LanguageIcon />} 
              label={t('BAHASA')}
              iconPosition="start" 
              sx={{ minHeight: '48px' }}
            />
            {isAdmin && (
              <Tab 
                icon={<AdminPanelSettingsIcon />} 
                label={t('ADMIN PANEL')}
                iconPosition="start" 
                sx={{ minHeight: '48px' }}
              />
            )}
          </Tabs>
        </Box>
      </Box>
      <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default Settings;
